import { BaseComponent } from '../base-component/base-component';
import { OfferTypeChecker } from '../../common/utils/offer-type-util';
import { OrderHistory as IOrderHistory, UserOrderDetails } from '../order-history/order-history.interface';
import { customElement, property } from 'lit/decorators.js';
import { expirationDate, isExpired } from '../my-account/my-digital-products/utils/my-digital-product.util';
import { formatDateDefault, formattedAuthors } from '../../common/utils/util';
import { getBadgeStyleFromLineStatusItem, getOrderUiStatus } from '../order-history/utils/order-history.util';
import { getCartItemFormat, getPriceWithCurrency } from '../../common/utils/cart-util';
import {
  CREATE_CUSTOM_CONTRIBUTOR_LABEL,
  getCreateCustomImage,
  renderCreateCustomProperty,
} from './utils/create-custom-product.util';
import { getMyBookshelfLaunchCta } from '../my-bookshelf/utils/my-bookshelf.util';
import { html, nothing, TemplateResult } from 'lit';

@customElement('mhecomm-create-custom-order-item-display')
export class CreateCustomOrderItemDisplay extends BaseComponent {
  @property({ type: Function })
  cta?: Function;

  @property({ type: Boolean })
  isMyDigitalProduct = false;

  @property({ type: Object })
  item?: UserOrderDetails;

  @property({ type: Object })
  order?: IOrderHistory;

  private isCreateEbook = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.isCreateEbook = OfferTypeChecker.isCreateEbook(this.item?.offerType);
  }

  renderComp(): TemplateResult | typeof nothing {
    if (!this.item || !this.order) {
      return nothing;
    }

    return this.isMyDigitalProduct
      ? this.myDigitalProduct(this.order, this.item, this.isCreateEbook)
      : this.orderHistory(this.order, this.item, this.isCreateEbook);
  }

  /**
   * Displays my digital products item details for Create/custom.
   *
   * @param order
   * @param item
   * @param isCreateEbook
   */
  private myDigitalProduct(order: IOrderHistory, item: UserOrderDetails, isCreateEbook: boolean): TemplateResult {
    return html`
      <div class="row p-3">
        <div class="col-4 col-sm-3 col-md-2">
          ${getCreateCustomImage({
            altText: item.productDescription,
            createCoverImages: item.createCustomProductMetadata?.createCoverImages,
            identifier: item.isbn13 || '',
            image: item.coverImage,
            isCreateEbook: isCreateEbook,
          })}
        </div>
        <div class="col-8 col-sm-9 col-md-7">
          <h2 class="h4 fs-large fw-semibold">
            <span data-automation-id="item-title-${order.webOrderNo}-${item.isbn10}">${item.productDescription}</span>
          </h2>
          ${isCreateEbook ? this.renderCreateEbookMetadata(item) : this.renderCustomMetadata(item)}
          ${item.terms
            ? html` <div class="mt-2">
                ${isExpired(order.orderedDate, item.terms, item.licenseEndDate)
                  ? html`<span class="badge rounded-pill bg-danger text-wrap">Expired</span>`
                  : html`<span class="badge rounded-pill text-dark text-wrap bg-info"
                      >Expires on ${formatDateDefault(expirationDate(order.orderedDate, item.terms))}</span
                    >`}
              </div>`
            : ''}
        </div>
        ${item.terms && isExpired(order.orderedDate, item.terms, item.licenseEndDate)
          ? nothing
          : html`
              <div class="col-12 col-md-3 my-auto text-md-end px-2 px-md-0 px-xl-2">
                <hr class="hr-primary d-md-none" />
                ${isCreateEbook ? getMyBookshelfLaunchCta(order, item, this.cta!) : ''}
              </div>
            `}
      </div>
      <div class="row p-3">
        <mhecomm-create-custom-product-supplements
          .supplements="${item.createCustomProductMetadata?.supplements}"
          ?showDivider=${true}
          headerLevel="h3">
        </mhecomm-create-custom-product-supplements>
      </div>
      <hr class="hr-primary m-0" />
    `;
  }

  /**
   * Displays the order history item details for Create/custom.
   *
   * @param order
   * @param item
   * @param isCreateEbook
   */
  private orderHistory(order: IOrderHistory, item: UserOrderDetails, isCreateEbook: boolean): TemplateResult {
    return html`
      <div class="row p-3">
        <div class="col-4 col-sm-3 col-md-2">
          ${getCreateCustomImage({
            altText: item.productDescription,
            createCoverImages: item.createCustomProductMetadata?.createCoverImages,
            identifier: item.isbn13 || '',
            image: item.coverImage,
            isCreateEbook: isCreateEbook,
          })}
        </div>
        <div class="col-8 col-sm-9 col-md-4">
          <div class="fs-large mb-2">
            <h3 class="h4 fs-large fw-semibold mb-0">
              <span data-automation-id="item-title-${order.webOrderNo}-${item.isbn13}">${item.productDescription}</span>
            </h3>
            <span class="badge mt-2 ${getBadgeStyleFromLineStatusItem(order, item)}">
              ${getOrderUiStatus(order, item)}
            </span>
          </div>
          ${isCreateEbook ? this.renderCreateEbookMetadata(item) : this.renderCustomMetadata(item)}
        </div>
        <div class="col-12 col-sm-12 col-md-3">
          <hr class="hr-primary d-md-none" aria-hidden="true" />
          <div class="row">
            <div class="col-8 col-sm-8 col-md-12 col-lg-12">
              <div class="row pb-3">
                <div class="d-flex justify-content-start justify-content-md-end mb-0">
                  <span>${item.isDiscountedBundle ? 'Bundle Offer' : 'Price'}:&nbsp;</span>
                  <span class="fw-semibold fs-large">
                    ${getPriceWithCurrency(order.currencyCode, item.price * item.itemQuantity)}
                  </span>
                </div>
              </div>
            </div>
            ${item.amountRefunded > 0
              ? html` <div class="col-4 col-sm-4 col-md-12 col-lg-12">
                  <div class="d-flex justify-content-end">
                    <div class="fw-semibold">Return Amount:&nbsp;</div>
                    ${getPriceWithCurrency(order.currencyCode, item.amountRefunded)}
                  </div>
                </div>`
              : nothing}
          </div>
        </div>
        <div class="col-12 col-md-3 text-md-end px-2 px-md-0 px-xl-2">
          ${isCreateEbook ? getMyBookshelfLaunchCta(order, item, this.cta!) : ''}
        </div>
      </div>
      <div class="row p-3">
        <mhecomm-create-custom-product-supplements
          .supplements="${item.createCustomProductMetadata?.supplements}"
          ?showDivider=${true}
          headerLevel="h4">
        </mhecomm-create-custom-product-supplements>
      </div>
      <hr class="hr-primary m-0" aria-hidden="true" />
    `;
  }

  /**
   * Displays metadata specific to Create ebooks (custombook offer type).
   *
   * @param item
   */
  private renderCreateEbookMetadata(item: UserOrderDetails): TemplateResult {
    const createCustomMetadata = item.createCustomProductMetadata;
    const instructor = createCustomMetadata?.instructorNames?.[0] ?? undefined;

    return html`
      ${renderCreateCustomProperty(createCustomMetadata?.school, 'name', 'School', ':')}
      ${renderCreateCustomProperty(createCustomMetadata, 'department', 'Department', ':')}
      ${instructor ? html`<div><strong> Instructor: </strong>${instructor.replace('null', '').trim()}</div>` : nothing}
      ${renderCreateCustomProperty(createCustomMetadata?.courses?.[0], 'courseNo', 'Course number', ':')}
      ${renderCreateCustomProperty(item, 'isbn13', 'ISBN13', ':')}
      <div><strong> Type: </strong>eBook</div>
      ${!this.isMyDigitalProduct && item.copyrightYear ? html`<div>©${item.copyrightYear}</div>` : nothing}
      ${item.formatDescription
        ? html`<div><span class="fw-bold">Format:</span> ${item.formatDescription}</div>`
        : nothing}
    `;
  }

  /**
   * Displays metadata for custom products that are not Create ebooks (privatebook offer type).
   *
   * @param item
   */
  private renderCustomMetadata(item: UserOrderDetails): TemplateResult {
    const createCustomMetadata = item.createCustomProductMetadata;

    return html`
      ${renderCreateCustomProperty(createCustomMetadata?.courses?.[0], 'courseNo', 'Course number', ':')}
      ${renderCreateCustomProperty(item, 'isbn13', 'ISBN13', ':')}
      ${item.authors ? html`<div><strong>${CREATE_CUSTOM_CONTRIBUTOR_LABEL}:</strong> ${formattedAuthors(item.authors)}</div>` : nothing}
      ${!this.isMyDigitalProduct && item.copyrightYear ? html`<div>©${item.copyrightYear}</div>` : nothing}
      ${item.formatDescription
        ? html`
            <div>
              <span class="fw-bold">Format:</span>
              ${getCartItemFormat(item.formatDescription, item.formatCode, item.virtual)}
            </div>
          `
        : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-create-custom-order-item-display': CreateCustomOrderItemDisplay;
  }
}
